import * as Dialog from '@radix-ui/react-dialog';
import { forwardRef, useEffect, useRef, useState } from 'react';

import {
  useMonkeysBaseAppearance,
  useMonkeysComponentAttributes,
  useMonkeysLocaleMessages,
} from '../../provider/context';
import { useBaseOverlayPortal } from './internal/overlayPortal';
import type { BaseControlTone, BaseDialogProps } from './types';
import {
  getBaseButtonToneClassName,
  resolveBaseAppearance,
} from './theme';
import { cn, hasRenderableNode } from './utils';

const dialogTriggerTones = [
  'default',
  'primary',
  'success',
  'warning',
  'danger',
  'muted',
] as const;

function validateBoolean(value: unknown, name: string) {
  if (typeof value !== 'boolean') {
    throw new TypeError(`BaseDialog ${name} must be a boolean.`);
  }
}

function validateTriggerTone(value: unknown) {
  if (
    !dialogTriggerTones.includes(
      value as (typeof dialogTriggerTones)[number],
    )
  ) {
    throw new TypeError(
      'BaseDialog triggerTone must be "default", "primary", "success", "warning", "danger", or "muted".',
    );
  }
}

function renderTrigger({
  trigger,
  triggerLabel,
  triggerIcon,
  triggerTone,
  triggerDisabled,
  theme,
}: Pick<
  BaseDialogProps,
  'trigger' | 'triggerDisabled' | 'triggerIcon' | 'triggerLabel'
> & {
  triggerTone: BaseControlTone;
  theme: ReturnType<typeof resolveBaseAppearance>;
}) {
  if (hasRenderableNode(trigger)) {
    return (
      <span
        role="button"
        tabIndex={triggerDisabled ? -1 : 0}
        aria-disabled={triggerDisabled || undefined}
        onKeyDown={(event) => {
          if (triggerDisabled) return;
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            event.currentTarget.click();
          }
        }}
      >
        {trigger}
      </span>
    );
  }

  if (!hasRenderableNode(triggerLabel)) return null;

  return (
    <button
      type="button"
      disabled={triggerDisabled}
      aria-label={
        typeof triggerLabel === 'string' && triggerLabel.trim()
          ? triggerLabel
          : undefined
      }
      className={cn(
        theme.slots.buttonBase,
        getBaseButtonToneClassName(theme, triggerTone),
        triggerDisabled && theme.slots.buttonDisabled,
      )}
      style={{
        ...theme.styles.button,
        ...(triggerDisabled ? theme.styles.buttonDisabled : undefined),
      }}
    >
      {hasRenderableNode(triggerIcon) ? (
        <span data-slot="icon" className={theme.slots.buttonIcon}>
          {triggerIcon}
        </span>
      ) : null}
      <span data-slot="label" className={theme.slots.buttonLabel}>
        {triggerLabel}
      </span>
    </button>
  );
}

export const BaseDialog = forwardRef<HTMLDivElement, BaseDialogProps>(
  function BaseDialog(
    {
      open,
      defaultOpen = false,
      title,
      description,
      header,
      footer,
      trigger,
      triggerLabel,
      triggerIcon,
      triggerTone = 'default',
      triggerDisabled = false,
      closeLabel,
      closeOnEscape = true,
      closeOnOverlayClick = true,
      showCloseButton = true,
      keepMounted = false,
      trapFocus = true,
      restoreFocus = true,
      initialFocus = 'first-interactive',
      initialFocusRef,
      zIndex,
      portal = false,
      appearance,
      className,
      classNames,
      style,
      children,
      onOpenChange,
      id,
      ...dialogProps
    },
    ref,
  ) {
    if (open !== undefined) validateBoolean(open, 'open');
    validateBoolean(defaultOpen, 'defaultOpen');
    validateBoolean(triggerDisabled, 'triggerDisabled');
    validateBoolean(closeOnEscape, 'closeOnEscape');
    validateBoolean(closeOnOverlayClick, 'closeOnOverlayClick');
    validateBoolean(showCloseButton, 'showCloseButton');
    validateBoolean(keepMounted, 'keepMounted');
    validateBoolean(trapFocus, 'trapFocus');
    validateBoolean(restoreFocus, 'restoreFocus');
    validateBoolean(portal, 'portal');
    validateTriggerTone(triggerTone);
    if (
      initialFocus !== 'first-interactive' &&
      initialFocus !== 'container' &&
      initialFocus !== 'explicit'
    ) {
      throw new TypeError(
        'BaseDialog initialFocus must be "first-interactive", "container", or "explicit".',
      );
    }
    if (zIndex !== undefined && !Number.isFinite(zIndex)) {
      throw new TypeError('BaseDialog zIndex must be a finite number.');
    }

    const messages = useMonkeysLocaleMessages().dialog;
    const resolvedAppearance = useMonkeysBaseAppearance(appearance);
    const componentAttributes = useMonkeysComponentAttributes();
    const resolvedCloseLabel =
      closeLabel === undefined ? messages.close : closeLabel;
    const theme = resolveBaseAppearance(resolvedAppearance);
    const overlayPortal = useBaseOverlayPortal({ disabled: !portal });
    const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
    const panelRef = useRef<HTMLDivElement | null>(null);
    const returnFocusRef = useRef<HTMLElement | null>(null);
    const isControlled = open !== undefined;
    const isOpen = isControlled ? open : uncontrolledOpen;
    const portalState =
      portal && overlayPortal.shouldPortal ? 'portaled' : 'inline';
    const hasTitle = hasRenderableNode(title);
    const hasDescription = hasRenderableNode(description);
    const hasCustomHeader = hasRenderableNode(header);

    if (
      showCloseButton &&
      (typeof resolvedCloseLabel !== 'string' || !resolvedCloseLabel.trim())
    ) {
      throw new TypeError(
        'BaseDialog closeLabel must be a non-empty string when showCloseButton is true.',
      );
    }

    const handleOpenChange = (nextOpen: boolean) => {
      if (!isControlled) {
        setUncontrolledOpen(nextOpen);
      }
      onOpenChange?.(nextOpen);
    };

    useEffect(() => {
      if (!isOpen || !keepMounted) return undefined;
      const panel = panelRef.current;
      const ownerDocument = panel?.ownerDocument;
      const ownerWindow = ownerDocument?.defaultView;
      if (!panel || !ownerDocument || !ownerWindow) return undefined;

      returnFocusRef.current =
        ownerDocument.activeElement instanceof ownerWindow.HTMLElement
          ? ownerDocument.activeElement
          : null;
      const focusableSelector = [
        'a[href]',
        'button:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
      ].join(',');
      const focusInitial = () => {
        const firstInteractive = panel.querySelector<HTMLElement>(focusableSelector);
        const target =
          initialFocus === 'container'
            ? panel
            : initialFocus === 'explicit'
              ? initialFocusRef?.current ?? panel
              : firstInteractive ?? panel;
        target.focus();
      };
      const frame = ownerWindow.requestAnimationFrame(focusInitial);
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape' && closeOnEscape) {
          event.preventDefault();
          event.stopPropagation();
          handleOpenChange(false);
          return;
        }
        if (event.key !== 'Tab' || !trapFocus) return;
        const focusable = Array.from(
          panel.querySelectorAll<HTMLElement>(focusableSelector),
        );
        if (focusable.length === 0) {
          event.preventDefault();
          panel.focus();
          return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && ownerDocument.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && ownerDocument.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      };
      ownerWindow.addEventListener('keydown', handleKeyDown);
      return () => {
        ownerWindow.cancelAnimationFrame(frame);
        ownerWindow.removeEventListener('keydown', handleKeyDown);
        if (restoreFocus && returnFocusRef.current?.isConnected) {
          returnFocusRef.current.focus();
        }
      };
    }, [
      closeOnEscape,
      initialFocus,
      initialFocusRef,
      isOpen,
      keepMounted,
      restoreFocus,
      trapFocus,
    ]);

    const semanticHeader =
      hasCustomHeader || hasTitle || hasDescription ? (
        <div
          {...componentAttributes}
          data-monkeys-component="dialog"
          data-slot="header"
          data-state={isOpen ? 'open' : 'closed'}
          data-portal={portalState}
          className={cn(theme.slots.dialogHeader, classNames?.header)}
        >
          {hasCustomHeader ? header : null}
          {hasTitle ? (
            <Dialog.Title
              {...componentAttributes}
              data-monkeys-component="dialog"
              data-slot="title"
              data-state={isOpen ? 'open' : 'closed'}
              data-portal={portalState}
              className={
                hasCustomHeader
                  ? 'sr-only'
                  : cn(theme.slots.dialogTitle, classNames?.title)
              }
            >
              {title}
            </Dialog.Title>
          ) : null}
          {hasDescription ? (
            <Dialog.Description
              {...componentAttributes}
              data-monkeys-component="dialog"
              data-slot="description"
              data-state={isOpen ? 'open' : 'closed'}
              data-portal={portalState}
              className={
                hasCustomHeader
                  ? 'sr-only'
                  : cn(
                      theme.slots.dialogDescription,
                      classNames?.description,
                    )
              }
            >
              {description}
            </Dialog.Description>
          ) : null}
        </div>
      ) : null;

    const triggerNode = renderTrigger({
      trigger,
      triggerLabel,
      triggerIcon,
      triggerTone,
      triggerDisabled,
      theme,
    });

    const dialogLayers = isOpen || keepMounted ? (
      <>
        <Dialog.Overlay
          forceMount={keepMounted || undefined}
          hidden={!isOpen}
          {...componentAttributes}
          aria-hidden="true"
          data-monkeys-component="dialog"
          data-slot="overlay"
          data-portal={portalState}
          className={cn(theme.slots.dialogOverlay, classNames?.overlay)}
          style={{ ...theme.styles.dialogOverlay, zIndex }}
        />
        <div
          {...componentAttributes}
          hidden={!isOpen}
          data-monkeys-component="dialog"
          data-slot="content"
          data-state={isOpen ? 'open' : 'closed'}
          data-portal={portalState}
          className={cn(theme.slots.dialogContent, classNames?.content)}
          style={{ zIndex }}
        >
          <Dialog.Content
            forceMount={keepMounted || undefined}
            {...componentAttributes}
            {...(!hasTitle ? { 'aria-labelledby': undefined } : {})}
            {...(!hasDescription ? { 'aria-describedby': undefined } : {})}
            asChild
            onOpenAutoFocus={(event) => {
              if (keepMounted) event.preventDefault();
            }}
            onCloseAutoFocus={(event) => {
              if (keepMounted) event.preventDefault();
            }}
            onEscapeKeyDown={(event) => {
              if (keepMounted || !closeOnEscape) event.preventDefault();
            }}
            onPointerDownOutside={(event) => {
              if (!closeOnOverlayClick) event.preventDefault();
            }}
          >
            <div
              {...componentAttributes}
              ref={panelRef}
              tabIndex={-1}
              aria-modal="true"
              data-monkeys-component="dialog"
              data-slot="panel"
              data-portal={portalState}
              className={cn(theme.slots.dialogPanel, classNames?.panel)}
              style={theme.styles.dialogPanel}
            >
              {semanticHeader}

              {showCloseButton ? (
                <Dialog.Close asChild>
                  <button
                    {...componentAttributes}
                    type="button"
                    aria-label={resolvedCloseLabel}
                    title={resolvedCloseLabel}
                    data-monkeys-component="dialog"
                    data-slot="close"
                    data-state={isOpen ? 'open' : 'closed'}
                    data-portal={portalState}
                    className={cn(
                      theme.slots.dialogClose,
                      classNames?.close,
                    )}
                  >
                    ×
                  </button>
                </Dialog.Close>
              ) : null}

              <div
                {...componentAttributes}
                data-monkeys-component="dialog"
                data-slot="body"
                data-state={isOpen ? 'open' : 'closed'}
                data-portal={portalState}
                className={cn(theme.slots.dialogBody, classNames?.body)}
              >
                {children}
              </div>

              {hasRenderableNode(footer) ? (
                <div
                  {...componentAttributes}
                  data-monkeys-component="dialog"
                  data-slot="footer"
                  data-state={isOpen ? 'open' : 'closed'}
                  data-portal={portalState}
                  className={cn(theme.slots.dialogFooter, classNames?.footer)}
                >
                  {footer}
                </div>
              ) : null}
            </div>
          </Dialog.Content>
        </div>
      </>
    ) : null;

    return (
      <div
        {...componentAttributes}
        {...dialogProps}
        ref={ref}
        id={id}
        data-monkeys-component="dialog"
        data-slot="root"
        data-state={isOpen ? 'open' : 'closed'}
        data-portal={portalState}
        className={cn(theme.slots.dialogRoot, classNames?.root, className)}
        style={style}
      >
        <Dialog.Root
          open={isOpen}
          onOpenChange={handleOpenChange}
          modal={!keepMounted && trapFocus}
        >
          {triggerNode ? (
            <Dialog.Trigger
              {...componentAttributes}
              asChild
              disabled={triggerDisabled}
              data-monkeys-component="dialog"
              data-slot="trigger"
              data-portal={portalState}
              data-tone={triggerTone}
              data-disabled={triggerDisabled || undefined}
              className={cn(
                theme.slots.dialogTrigger,
                triggerDisabled && theme.slots.dialogTriggerDisabled,
                classNames?.trigger,
              )}
            >
              {triggerNode}
            </Dialog.Trigger>
          ) : null}
          {portal && overlayPortal.shouldPortal ? (
            <Dialog.Portal
              {...overlayPortal.radixPortalProps}
              forceMount={keepMounted || undefined}
            >
              {dialogLayers}
            </Dialog.Portal>
          ) : (
            dialogLayers
          )}
        </Dialog.Root>
      </div>
    );
  },
);
